/**
 * AI Visibility API client — separate file from seoApi.ts to keep this feature's
 * API surface explicit. All endpoints are mounted under /api/visibility.
 */

import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

// ─── Types matching backend service responses ─────────────────────────────────

export type EngineProvider = 'chatgpt';

export interface VisibilityScore {
  scoreNow: number;
  scoreLastWeek: number;
  trend: { weekOf: string; score: number }[];
  questionsAppearedNow: number;
  questionsTotal: number;
  firstRunAt: string | null;
  lastRunAt: string | null;
  /** False when no tracking prompts have ever been generated for this location —
   * distinguishes "not yet measured" from a genuine 0% score. */
  hasEverRun: boolean;
}

export interface PromptResult {
  appeared: boolean;
  position: number | null;
  topResult: string | null;
}

export interface TrackedQuestion {
  id: string;
  promptText: string;
  /** The saved search term this question was written for; null when it is not tied to one. */
  searchTerm: string | null;
  strategyItemIndex: number | null;
  userAdded: boolean;
  createdAt: string;
  results: {
    chatgpt: { now: PromptResult | null; prev: PromptResult | null };
  };
  consecutiveZeroWeeks: number | null;
}

export interface CompetitorShareRow {
  name: string;
  count: number;
  pct: number;
  isYou: boolean;
  isOther: boolean;
}

// ─── Client ──────────────────────────────────────────────────────────────────

export const visibilityApi = {
  getScore: async (locationId: string): Promise<ApiResponse<VisibilityScore>> => {
    const response = await apiClient.get<ApiResponse<VisibilityScore>>('/visibility/score', {
      params: { locationId },
    });
    return response.data;
  },

  getQuestions: async (locationId: string): Promise<ApiResponse<TrackedQuestion[]>> => {
    const response = await apiClient.get<ApiResponse<TrackedQuestion[]>>('/visibility/questions', {
      params: { locationId },
    });
    return response.data;
  },

  getCompetitorShare: async (locationId: string): Promise<ApiResponse<CompetitorShareRow[]>> => {
    const response = await apiClient.get<ApiResponse<CompetitorShareRow[]>>(
      '/visibility/competitor-share',
      { params: { locationId } },
    );
    return response.data;
  },
};
